/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { PrimaryButton } from '@fluentui/react/lib/Button';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Icon } from '@fluentui/react/lib/Icon';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DirectoriesTable } from 'src/appShell/globalHeader/globalHeaderBlades/settingsBlade/DirectoriesTable';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisText } from 'src/core/components/LuisText';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { AzureTenantStore } from 'src/core/stores/AzureTenantStore';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';

const messages = defineMessages({
    searchPlaceholder: { id: 'languageStudio.directoriesSettings.searchPlaceholder', defaultMessage: 'Search' },
    switchDirectory: { id: 'languageStudio.directoriesSettings.switchDirectory', defaultMessage: 'Switch directory' },
    learnMore: { id: 'languageStudio.directoriesSettings.learnMore', defaultMessage: 'Learn more about directories.' },
    viewInAzure: { id: 'languageStudio.directoriesSettings.viewInAzure', defaultMessage: 'View all properties in Azure Portal' },
    currentDirectoryLabel: { id: 'languageStudio.directoriesSettings.currentDirectoryLabel', defaultMessage: 'Current Directory:' },
    description: {
        id: 'languageStudio.directoriesSettings.description',
        defaultMessage: `The directory you choose will impact subscription, resource group,
        and region filters that are available when you view resources.`
    }
});

const containerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { flex: 1, minHeight: 0 } });
const paginationStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { margin: '0 !important' } });
const tableContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { flex: 1, minHeight: 0, marginTop: '0 !important' }
});

export const DirectoriesSettings = observer(() => {
    const { localize } = useLocalization();
    const [query, setQuery] = React.useState('');
    const [sortKey, setSortKey] = React.useState<SortKeyType<AzureTenantStore>>({ key: 'name', order: 'asc' });

    const { dispatcher, rootStore } = useGlobalState();
    const switchDirectoryTracker = useProgressTracker();
    const { selection, selectedItems } = useSelection<AzureTenantStore>();

    const azureSession = rootStore.azureStore.session;
    const tenantsStoreHouse = rootStore.azureStore.tenantStoreHouse;
    const currentDirectory = rootStore.azureStore.tenantStoreHouse.findById(azureSession.tenantId);
    const isSwitchDisabled = !selectedItems.length || selectedItems[0]?.id === currentDirectory.id || switchDirectoryTracker.isRunning;
    const filteredDirectories = orderBy(
        substringSearchStrategy(tenantsStoreHouse.items, query, 'name'),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
    );
    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(filteredDirectories, 7);

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        selection.setAllSelected(false);
    };

    const switchDirectory = (tenantStore: AzureTenantStore) =>
        dispatcher.dispatch('switchAzureTenantInSession', { id: tenantStore.id, tracker: switchDirectoryTracker });

    const handleSortChange = (column: IColumn) => {
        setSortKey({ key: column.key as keyof AzureTenantStore, order: column.isSortedDescending ? 'asc' : 'desc' });
        selection.setAllSelected(false);
    };

    return (
        <Stack styles={containerStyles} tokens={{ childrenGap: 18 }}>
            <LuisText>
                {localize(messages.description)}
                &nbsp;
                <ExternalLink href="https://aka.ms/LanguageAzureActiveDirectory ">{localize(messages.learnMore)}</ExternalLink>
            </LuisText>

            <LuisText>
                <LuisText variant="mediumPlus" fontWeight={600}>
                    {localize(messages.currentDirectoryLabel)}
                </LuisText>
                &nbsp;
                <LuisText variant="mediumPlus">{currentDirectory?.name}</LuisText>
            </LuisText>

            <LuisCommandBar includeSearchBar searchBarAlwaysExpanded query={query} onQueryChange={setQuery} items={[]} />

            <Stack styles={tableContainerStyles}>
                <DirectoriesTable
                    items={pageItems}
                    sortKey={sortKey}
                    selection={selection}
                    areItemsFiltered={Boolean(query)}
                    onSortColumn={handleSortChange}
                    overallItemsCount={filteredDirectories.length}
                    isLoading={tenantsStoreHouse.tracker.isRunning}
                />
            </Stack>

            <Stack horizontal horizontalAlign="space-between">
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 38 }}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                        <PrimaryButton disabled={isSwitchDisabled} onClick={() => switchDirectory(selectedItems[0])}>
                            {localize(messages.switchDirectory)}
                        </PrimaryButton>
                        {switchDirectoryTracker.isRunning && <Spinner size={SpinnerSize.small} />}
                    </Stack>

                    <ExternalLink href="https://ms.portal.azure.com/#settings/directory">
                        <Stack horizontal verticalAlign="center">
                            <LuisText>{localize(messages.viewInAzure)}</LuisText>
                            &nbsp;
                            <Icon iconName="MiniExpand" />
                        </Stack>
                    </ExternalLink>
                </Stack>

                <TablePagination
                    pageCount={pageCount}
                    currentPage={currentPage}
                    containerStyles={paginationStyles}
                    onPageChange={p => handlePageChange(p.selected)}
                />
            </Stack>
        </Stack>
    );
});
